import { type JanusCtx, type Row, definitionNotHeld, ecId, handBack, inventoryId, upper } from './context.js';
import { janus } from './janus.js';
import { frameCopyOnRead } from './frame-snapshot.js';
import { expandPlacedAoeTargets, type PlacedAoeReaders } from './aoe/expand-placed-aoe-targets.js';
import { inventoryDamageTarget } from '../inventory/ability-damage.js';
import { enqueueCaused } from '../inventory/payload-chain.js';
import { PAYLOAD_MAX_RESOLUTIONS, planPayloadDrain } from '../inventory/destruction-payload.js';

/** Destroy's job ordering and placement, resolved inside the attack's existing synthetic/live frame and single save. */
export function drainAbilityDestructionPayloads(ctx: JanusCtx & { read: JanusCtx['read'] & { placedAoe?: PlacedAoeReaders } }): void {
  const drained = ctx.destructionPayloadsDrained ??= new Set<string>();
  const pending = () => ctx.inventoryChanged.flatMap(change => change.destruction ? enqueueCaused(change.destruction, []) : [])
    .filter(job => !drained.has(job.source_inventory_id));
  const queue = pending();
  for (let next = 0; next < queue.length; next++) {
    const job = queue[next]!;
    if (drained.has(job.source_inventory_id)) continue;
    if (drained.size >= PAYLOAD_MAX_RESOLUTIONS) handBack('payload:payload-budget-exhausted');
    drained.add(job.source_inventory_id);
    const plan = planPayloadDrain(ctx.read, job);
    if ('reason' in plan) {
      if (plan.definitionIds?.length) definitionNotHeld('inventory-damage:definition-not-held', plan.definitionIds[0]);
      handBack(`payload:${plan.reason}`);
    }
    for (const step of plan.steps) {
      if (step.action === 'skip') continue;
      const caster = ctx.members.get(upper(job.attributed_caster.id));
      if (!caster) handBack('payload:creature-caster-not-held');
      const ability = ctx.read.definition(step.ability_id)!;
      const placed = ctx.read.placedAoe;
      if (!placed) handBack('payload:placed-readers-missing');
      const encounter: Row = { ...ctx.encounter, encounter_creatures_arr: [...ctx.members.values()] };
      const expanded = expandPlacedAoeTargets({ ...placed!, encounter, placementFailure: () => null },
        step.casting_targets, caster!, ability, true);
      if (expanded.refusal) handBack('payload:expansion-refusal');
      const targets = expanded.targets.filter(row => inventoryId(row) !== job.source_inventory_id).map(row => {
        if (ecId(row)) return ctx.members.get(ecId(row))!;
        const target = inventoryDamageTarget(ctx.read, row, ctx.encounter);
        const floor = ctx.encounter.encounter_inventories_arr as Row[];
        const index = floor.findIndex(entry => inventoryId(entry) === inventoryId(target));
        if (index >= 0) floor[index] = target;
        return target;
      });
      const prior = { trigger: ctx.trigger, statusConnectedId: ctx.statusConnectedId, dispatchRowStart: ctx.dispatchRowStart,
        dispatchTargetIds: ctx.dispatchTargetIds, triggerIncitingTarget: ctx.triggerIncitingTarget };
      ctx.trigger = 'destruction_effect_ability'; ctx.triggerIncitingTarget = null;
      ctx.statusConnectedId = null; ctx.dispatchRowStart = ctx.rows.length;
      try {
        janus(ctx, { casterId: ecId(caster!), targetIds: targets.map(row => ecId(row) || inventoryId(row)), abilityId: step.ability_id,
          rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null,
          castingTargets: step.casting_targets }, frameCopyOnRead(ability, new Map(), { catalogs: 'share' }).encounter, caster!, targets);
      } finally { Object.assign(ctx, prior); }
      queue.push(...pending().filter(candidate => !queue.some(held => held.source_inventory_id === candidate.source_inventory_id)));
    }
  }
}
